import {
    DefaultDarkColor,
    DefaultLightColor
} from '@office-start/feedback-sdk-service';
import {
    FeedbackAgeGroup,
    FeedbackAuthenticationType,
    FeedbackHostPlatformType,
    FeedbackPolicyValue,
    FeedbackScreenshotSourceType,
    FeedbackUiType,
    HARMONY_APP_ID,
    IFeedbackInitOptions
} from '@office-start/feedback-sdk-types';
import { DataSecurityLevel } from '@office-start/instrumentation-types';
import { logUserAction } from '@office-start/telemetry-service';
import { AdminPolicy } from './admin-policy.service';

const feedbackModalDismissed = (result: 'Close') => {
    logUserAction(
        {
            eventName: 'ShellOcvFeedbackModalDismissed',
            area: 'ShellHeader',
            id: 'ShellOcvFeedbackModalDismissed',
            result,
            target: 'FeedbackDialogHarmony'
        },
        DataSecurityLevel.Enhanced
    );
};

function onDismissPrint() {
    feedbackModalDismissed('Close'); //logging for "dismiss" action"
}

export type defaultFeedbackOptionsParams = {
    ocvLaunchOptions: any;
    policy: AdminPolicy | null | undefined;
    FeedbackInitOptions: IFeedbackInitOptions;
    darkMode: boolean | undefined;
};

export const defaultFeedbackOptions = ({
    ocvLaunchOptions,
    policy,
    FeedbackInitOptions,
    darkMode
}: defaultFeedbackOptionsParams): IFeedbackInitOptions => {
    FeedbackInitOptions = {
        appId: HARMONY_APP_ID, //This value should not change. It is the appId for the feedback module,
        ageGroup: FeedbackAgeGroup.Undefined, //default
        authenticationType: FeedbackAuthenticationType.AAD, //default
        callbackFunctions: {
            onDismiss: onDismissPrint
        },
        isProduction: true,
        clientName: 'Harmony',
        feedbackConfig: {
            appData: ocvLaunchOptions.applicationGroup.appData,
            complianceChecks: {
                connectedExperiences: policy?.ConnectedExperience.PolicyValue === 1 ? FeedbackPolicyValue.Enabled : FeedbackPolicyValue.Disabled,
                policyAllowContact: FeedbackPolicyValue.Disabled,
                policyAllowContent: policy?.LogCollection.PolicyValue === 1 ? FeedbackPolicyValue.Enabled : FeedbackPolicyValue.Disabled,
                policyAllowFeedback: policy?.SendFeedback.PolicyValue === 1 ? FeedbackPolicyValue.Enabled : FeedbackPolicyValue.Disabled,
                policyAllowScreenshot: policy?.ScreenShot.PolicyValue === 1 ? FeedbackPolicyValue.Enabled : FeedbackPolicyValue.Disabled,
                policyAllowSurvey: policy?.SendSurvey.PolicyValue === 1 ? FeedbackPolicyValue.Enabled : FeedbackPolicyValue.Disabled,
                policyEmailCollectionDefault: policy?.EmailCollection.PolicyValue === 1 ? FeedbackPolicyValue.Enabled : FeedbackPolicyValue.Disabled
            }, // complianceChecks is required for AAD users. The host app needs to query OCPS API to get the compliance checks and pass on to the feedback module
            zIndex: 1000001,
            feedbackUiType: FeedbackUiType.Modal,
            hostPlatform: FeedbackHostPlatformType.Web,
            isDisplayed: true,
            isFileUploadEnabled: false,
            isMyFeedbackEnabled: true,
            isScreenshotEnabled: true,
            isScreenRecordingEnabled: false,
            isThankYouPageDisabled: false,
            screenshotInfo: {
                providedScreenshotType: FeedbackScreenshotSourceType.AutoCapture
            }
        },
        telemetry: {
            // Telemetry is optional. If we want it, we need to set our own telemetry data
            audienceGroup: ocvLaunchOptions.telemetryGroup.audienceGroup,
            browser: ocvLaunchOptions.webGroup.browser,
            deviceType: ocvLaunchOptions.telemetryGroup.deviceType,
            featureArea: ocvLaunchOptions.telemetryGroup.featureArea,
            flights: ocvLaunchOptions.telemetryGroup.flights,
            officeBuild: ocvLaunchOptions.telemetryGroup.officeBuild,
            platform: ocvLaunchOptions.telemetryGroup.platform,
            processSessionId: ocvLaunchOptions.telemetryGroup.processSessionId,
            sourcePageName: ocvLaunchOptions.webGroup.sourcePageName
        },
        tenantId: ocvLaunchOptions.telemetryGroup.tenantId, // tenantId must be dynamically fetched
        themeOptions: {
            // Theme gets updated when we switch between dark mode and light mode
            baseTheme: darkMode ? DefaultDarkColor : DefaultLightColor
        },
        userId: ocvLaunchOptions.telemetryGroup.loggableUserId // userId must be dynamically fetched
    };

    return FeedbackInitOptions;
};
